import { CellContext, ColumnDef, filterFns } from '@tanstack/react-table';

import { convertedQtyFormatterConfig } from '@/config/config';
import { largeCell, mediumCell, smallCell, xSmallCell } from '@/constants/table';
import { Modify } from '@/model/common';
import { Instrument, TradesResponse } from '@/types/sso';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';

export type TableType = 'FX' | 'CFD' | 'Option';

export interface TradeBookingRow
    extends Modify<
        TradesResponse,
        {
            tableUTI: string;
            tableType: TableType;
            tablePrice: string;
            tableStrikePrice: string;
            tableAmount: string;
            tableTotalFees: string;
            tableFeeCcy: string;
            tableContraAmount: string;
            tableLowerBarrier: string;
            tableUpperBarrier: string;
            tableExecutionTime: string;
            tableSymbol: string;
            tableSubType: string;
            tableSide: string;
            tradeStatus: string;
            entityId: number;
            entityName: string;
            counterpartid: string | number;
            counterpartName: string;
            accountId: string | number;
            accountName: string;
            contraAccountId: string | number;
            contraAccountName: string;
            amount: string | number;
            contraAmount: string | number;
            price: string | number;
            executionTime: string;
            lowerBarrier: string | number;
            upperBarrier: string | number;
        }
    > {}

interface GetTradeBookingColumnsParams {
    lang: UseLanguageReturn;
}

const commonColumns = (lang: UseLanguageReturn): ColumnDef<TradeBookingRow>[] => [
    {
        ...mediumCell,
        filterFn: filterFns.equalsString,
        accessorKey: 'tradeStatus',
        header: lang.blotters.status,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...smallCell,
        accessorKey: 'tradeId',
        header: lang.blotters.tradeId,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...smallCell,
        accessorKey: 'tradeDate',
        header: lang.blotters.tradeDate,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...xSmallCell,
        accessorKey: 'tableType',
        header: lang.blotters.type,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...smallCell,
        accessorKey: 'tableSubType',
        header: lang.blotters.subType,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue() || '-'
    },
    {
        ...smallCell,
        accessorKey: 'settlementDate',
        header: lang.blotters.valueDate,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    }
];

const commonCollumns2 = (lang: UseLanguageReturn): ColumnDef<TradeBookingRow>[] => [
    {
        accessorKey: 'orderId',
        header: lang.blotters.orderId,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        accessorKey: 'executionId',
        header: lang.blotters.executionId,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...smallCell,
        accessorKey: 'tableSide',
        header: lang.blotters.side,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...smallCell,
        accessorKey: 'tableSymbol',
        header: lang.blotters.symbol,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...largeCell,
        accessorKey: 'tableAmount',
        header: lang.blotters.amount,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        ...largeCell,
        accessorKey: 'tableContraAmount',
        header: lang.blotters.contraAmount,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    }
];

const commonCollumns3 = (lang: UseLanguageReturn): ColumnDef<TradeBookingRow>[] => [
    {
        ...largeCell,
        accessorKey: 'tableExecutionTime',
        header: lang.blotters.executionTime,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        accessorKey: 'tradeLinkId',
        header: lang.blotters.tradeLinkId,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    },
    {
        filterFn: filterFns.equalsString,
        accessorKey: 'tableUTI',
        header: lang.blotters.uti,
        cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
    }
];

export const getTradeBookingColumns = ({ lang }: GetTradeBookingColumnsParams) => {
    const columns: ColumnDef<TradeBookingRow>[] = [
        ...commonColumns(lang),
        ...commonCollumns2(lang),
        {
            ...mediumCell,
            accessorKey: 'price',
            header: lang.blotters.price,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...smallCell,
            accessorKey: 'tableTotalFees',
            header: lang.blotters.fees,
            cell: (info: CellContext<TradeBookingRow, any>) => {
                const hasTotalFees = !!info.row.original.totalFees;
                return hasTotalFees ? info.getValue() : '-';
            }
        },
        {
            ...smallCell,
            accessorKey: 'tableFeeCcy',
            header: lang.blotters.feesCcy,
            cell: (info: CellContext<TradeBookingRow, any>) => {
                const hasTotalFees = !!info.row.original.totalFees;
                return hasTotalFees ? info.getValue() || '-' : '-';
            }
        },
        ...commonCollumns3(lang)
    ];

    return columns;
};

export const getOptionTradeBookingColumns = ({ lang }: GetTradeBookingColumnsParams) => {
    const columns: ColumnDef<TradeBookingRow>[] = [
        ...commonColumns(lang),
        {
            ...smallCell,
            accessorKey: 'expiryDate',
            header: lang.blotters.expiryDate,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'optionStyle',
            header: lang.blotters.optionStyle,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...smallCell,
            accessorKey: 'optionType',
            header: lang.blotters.optionType,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'optionVariant',
            header: lang.blotters.optionVariant,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...smallCell,
            accessorKey: 'tableTotalFees',
            header: lang.blotters.fees,
            cell: (info: CellContext<TradeBookingRow, any>) => {
                const hasTotalFees = !!info.row.original.totalFees;
                return hasTotalFees ? info.getValue() : '-';
            }
        },
        {
            ...smallCell,
            accessorKey: 'tableFeeCcy',
            header: lang.blotters.feesCcy,
            cell: (info: CellContext<TradeBookingRow, any>) => {
                const hasTotalFees = !!info.row.original.totalFees;
                return hasTotalFees ? info.getValue() || '-' : '-';
            }
        },
        ...commonCollumns2(lang),
        {
            ...mediumCell,
            accessorKey: 'tablePrice',
            header: lang.blotters.price,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'tableStrikePrice',
            header: lang.blotters.strikePrice,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'tableLowerBarrier',
            header: lang.blotters.lowerBarrier,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'tableUpperBarrier',
            header: lang.blotters.upperBarrier,
            cell: (info: CellContext<TradeBookingRow, any>) => info.getValue()
        },
        ...commonCollumns3(lang)
    ];

    return columns;
};

export const convertToTradeBookingRow = (
    tradeBooking: TradesResponse,
    legalEntityId: string,
    dateFormatter: Intl.DateTimeFormat,
    executionTimeFormatter: Intl.DateTimeFormat,
    instruments: Instrument[],
    locale: string
): TradeBookingRow => {
    const amount = tradeBooking.quantity || tradeBooking.currencyOneQuantity;
    const execId = tradeBooking.executionId
        ?.replaceAll('-', '')
        ?.replaceAll('_c', '')
        ?.replaceAll('Contra', '')
        ?.toUpperCase();

    const { amountFormatter, contraAmountFormatter } = getInstrumentConfigs(instruments, tradeBooking, locale);
    const priceFormatter = new Intl.NumberFormat(locale, convertedQtyFormatterConfig);

    const isCryptoSpot = tradeBooking.type === 'FX' && tradeBooking.instrumentType === 'Crypto';
    const isCryptoOption = tradeBooking.type === 'Option' && tradeBooking.instrumentType === 'CryptoOption';

    const isUTIDisabled = isCryptoSpot || isCryptoOption;

    let tableSymbol = tradeBooking.assetName;
    if (tradeBooking.type === 'CfdTrade') {
        const symbolWithoutCfdPrefix = formatCfdSymbol(tradeBooking.instrumentSymbol);
        if (tradeBooking.instrumentType === 'Future') {
            tableSymbol = tradeBooking.instrumentSymbol;
        } else {
            tableSymbol = `${tradeBooking.assetName}/${symbolWithoutCfdPrefix.split('/')[1]}`;
        }
    }

    return {
        ...tradeBooking,
        tableType: switchType(tradeBooking.type),
        tableSubType: tradeBooking.instrumentType || '-',
        tablePrice: priceFormatter.format(tradeBooking.price),
        tableStrikePrice: tradeBooking.strikePrice ? priceFormatter.format(tradeBooking.strikePrice) : '-',
        tableAmount: amountFormatter.format(amount),
        tableTotalFees: priceFormatter.format(tradeBooking.totalFees || 0),
        tableFeeCcy: tradeBooking.feesCurrencyName,
        tableContraAmount: tradeBooking.currencyTwoQuantity
            ? contraAmountFormatter.format(tradeBooking.currencyTwoQuantity)
            : '-',
        tableLowerBarrier: tradeBooking.lowerBarrier ? priceFormatter.format(tradeBooking.lowerBarrier) : '-',
        tableUpperBarrier: tradeBooking.upperBarrier ? priceFormatter.format(tradeBooking.upperBarrier) : '-',
        tableExecutionTime: executionTimeFormatter.format(new Date(tradeBooking.executionTime)),
        tableSymbol,
        tableSide: amount > 0 ? 'Buy' : 'Sell',
        tableUTI: isUTIDisabled ? 'N/A' : `${legalEntityId}${execId}`,
        tradeStatus: tradeBooking.status,
        tradeDate: dateFormatter.format(new Date(tradeBooking.tradeDate)),
        expiryDate: dateFormatter.format(new Date(tradeBooking.expiryDate)),
        settlementDate: dateFormatter.format(new Date(tradeBooking.settlementDate)),
        contraAmount: tradeBooking.currencyTwoQuantity || '-',
        entityId: tradeBooking.entityId,
        orderId: tradeBooking.orderId || '-',
        entityName: '-',
        counterpartid: '-',
        counterpartName: '-',
        accountId: tradeBooking.accountId,
        accountName: '-',
        contraAccountId: '-',
        contraAccountName: '-',
        lowerBarrier: tradeBooking.lowerBarrier || '-',
        optionStyle: tradeBooking.optionStyle || '-',
        optionType: tradeBooking.optionType || '-',
        optionVariant: tradeBooking.optionVariant || '-',
        upperBarrier: tradeBooking.upperBarrier || '-',
        price: tradeBooking.price,
        amount
    };
};

function switchType(type: string): TableType {
    switch (type) {
        case 'FxTrade':
            return 'FX';
        case 'CfdTrade':
            return 'CFD';
        case 'FxOptionTrade':
            return 'Option';
        default:
            return 'FX';
    }
}

export function formatCfdSymbol(symbol: string): string {
    return symbol.replace(/^CFD\/|\/GCE$/gm, '');
}

function getInstrumentConfigs(instruments: Instrument[], tradeBooking: TradesResponse, locale: string) {
    const config = instruments?.find((instrument) => {
        const isEqualSymbol = instrument.symbol === `${tradeBooking.currencyOne}/${tradeBooking.currencyTwo}`;
        const isEqualCFD = instrument.symbol === `${tradeBooking.instrumentSymbol?.replace('/GCE', '')}`;
        return isEqualSymbol || isEqualCFD;
    });

    const ccy1Decimals = config ? (config.quantityDecimals >= 0 ? config?.quantityDecimals : 20) : 20;
    const ccy2Decimals = config ? (config.quantity2Decimals >= 0 ? config?.quantity2Decimals : 20) : 20;

    const amountFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: ccy1Decimals
    });

    const contraAmountFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: ccy2Decimals
    });

    return { amountFormatter, contraAmountFormatter };
}

export function getCurrencyConfigs(instrument: Instrument) {
    const ccy1Decimals = instrument.quantityDecimals >= 0 ? instrument.quantityDecimals : 20;
    const ccy2Decimals = instrument.quantity2Decimals >= 0 ? instrument.quantity2Decimals : 20;

    return { ccy1Decimals, ccy2Decimals };
}
